import { type CSSProperties, useContext } from 'react';

import type { TPostCommentsElement } from '@shared/dream-components';
import classnames from 'classnames';

import { PostDataContext } from '~/context/PostDataContext';
import type { PostCommunityComments } from '~/interfaces/post';

import { lazyBlock } from '../lazyBlock';

// Lazy because this skeleton ships on every reader page and the comments element is large. Named after the node type,
// so hydration and client navigations wait for the chunk on posts that render comments.
const ServerRenderedComments = lazyBlock('postComments', () =>
  import('./ServerRenderedComments').then((module) => module.default),
);

// SSR/Suspense fallback for the lazy PostComments node: keeps the #comments anchor and reserves the section's height.
type PostCommentsSkeletonProps = {
  node: TPostCommentsElement;
  communityComments: PostCommunityComments | null;
  // Set when the comments chunk failed, so the reserved space doesn't pulse forever.
  isStatic?: boolean;
};

// Measured [mobile, desktop] heights of the open community section; replies aren't in the post data, so a thread with replies still grows.
const COMMUNITY_CHROME = [224, 208];
const COMMUNITY_COMMENT = [148, 124];
const COMMUNITY_SHOW_MORE = 48;
const COMMUNITY_EMPTY = 54;
const COMMUNITY_VISIBLE_COMMENTS = 2;

const communityThreadStyle = (communityComments: PostCommunityComments): CSSProperties | undefined => {
  if (!communityComments.viewer_can_access || communityComments.comments_state !== 'open') return undefined;

  const count = communityComments.comments_count;
  const [mobile, desktop] = COMMUNITY_CHROME.map((chrome, index) => {
    if (count === 0) return chrome + COMMUNITY_EMPTY;
    const visible = Math.min(count, COMMUNITY_VISIBLE_COMMENTS);
    return chrome + visible * COMMUNITY_COMMENT[index] + (count > visible ? COMMUNITY_SHOW_MORE : 0);
  });
  return { '--comments-height': `${mobile}px`, '--comments-height-sm': `${desktop}px` } as CSSProperties;
};

// Measured heights of the native section while its list loads: header, comment form, list loading bars.
const NATIVE_LOADING_BLOCKS = ['h-[46px]', 'h-[118px]', 'h-44'];

const PostCommentsSkeleton = ({ node, communityComments, isStatic = false }: PostCommentsSkeletonProps) => {
  const blockClassName = classnames('w-full', { 'animate-pulse': !isStatic }, 'rounded-xl bg-black/5');

  const firstCommentsPage = useContext(PostDataContext)?.firstCommentsPage;
  if (!communityComments && firstCommentsPage) return <ServerRenderedComments node={node} page={firstCommentsPage} />;

  if (!communityComments) {
    const { gap, padding, backgroundColor, borderColor, borderStyle, borderRadius, borderWidth } = node.attrs;
    return (
      <section
        id="comments"
        className="flex w-full flex-col"
        style={{ gap, padding, backgroundColor, borderColor, borderStyle, borderRadius, borderWidth }}
        aria-hidden="true"
      >
        {NATIVE_LOADING_BLOCKS.map((height) => (
          <div key={height} className={classnames(height, blockClassName)} />
        ))}
      </section>
    );
  }

  const threadStyle = communityThreadStyle(communityComments);

  return (
    <section id="comments" className="mx-auto w-full max-w-2xl px-4 py-8" aria-hidden="true">
      <div
        className={classnames(
          threadStyle ? 'h-[var(--comments-height)] sm:h-[var(--comments-height-sm)]' : 'h-64',
          blockClassName,
        )}
        style={threadStyle}
      />
    </section>
  );
};

export default PostCommentsSkeleton;
